/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-16 15:00:00
 *
 *******************************************************/
// @flow

import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';

import apiService from 'src/services/api-service';
import {
  SI_CHECK_THEME_STATUS_REQUEST,
  SI_CHECK_THEME_STATUS_SUCCESS,
  SI_CHECK_THEME_STATUS_FAILURE,
  SI_THEME_INTEGRATION_SETUP_REQUEST,
  SI_THEME_INTEGRATION_SETUP_SUCCESS,
  SI_THEME_INTEGRATION_SETUP_FAILURE,
  SI_THEME_INTEGRATION_UPGRADE_REQUEST,
  SI_THEME_INTEGRATION_UPGRADE_SUCCESS,
  SI_THEME_INTEGRATION_UPGRADE_FAILURE,
  SI_THEME_INTEGRATION_SETTINGS_REQUEST,
  SI_THEME_INTEGRATION_SETTINGS_SUCCESS,
  SI_THEME_INTEGRATION_SETTINGS_FAILURE,
  SI_THEME_INTEGRATION_UPDATE_FIELD,
  SI_THEME_INTEGRATION_UPDATE_MULTI_FIELDS,
  SI_THEME_INTEGRATION_FEATURE_REQUEST,
  SI_THEME_INTEGRATION_FEATURE_SUCCESS,
  SI_THEME_INTEGRATION_FEATURE_FAILURE
} from 'stores/flow-type/shopify-integration/theme-integration.type';

/**
 * call to get list of all themes api (v3 api, v3 of update phase, not theme v3)
 * @return {object} The RSAA object for API middleware
 */
export const checkThemeIntegrationStatus = (i18n: I18n, themeId: Number) => {
  const params = { themeId };
  return apiService.sendRequest(
    'shopify-integration.theme-integration.check-integration-status',
    { params },
    [
      SI_CHECK_THEME_STATUS_REQUEST,
      {
        type: SI_CHECK_THEME_STATUS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_CHECK_THEME_STATUS_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ThemeSetup.Actions.getAllThemes'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to setup theme integration to specific store
 * @return {object} The RSAA object for API middleware
 */
export const themeIntegrationSetup = (
  i18n: I18n,
  themeId: String,
  templateId: String,
  themeNameLib: String,
  isDefault: boolean,
) => {
  const params = { themeId };
  const body = { templateId, isDefault, themeNameLib };
  return apiService.sendRequest(
    'shopify-integration.theme-integration.theme-integration-setup',
    { params, body, },
    [
      SI_THEME_INTEGRATION_SETUP_REQUEST,
      {
        type: SI_THEME_INTEGRATION_SETUP_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_THEME_INTEGRATION_SETUP_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ThemeSetup.Actions.getAllThemes'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to upgrade theme integration to specific store
 * @return {object} The RSAA object for API middleware
 */
export const themeIntegrationUpgrade = (
  i18n: I18n,
  themeId: String,
  templateId: String,
  version: String
) => {
  const params = { themeId };
  const body = { templateId, version };
  return apiService.sendRequest(
    'shopify-integration.theme-integration.theme-integration-upgrade',
    { params, body },
    [
      SI_THEME_INTEGRATION_UPGRADE_REQUEST,
      {
        type: SI_THEME_INTEGRATION_UPGRADE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_THEME_INTEGRATION_UPGRADE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ThemeSetup.Actions.getAllThemes'),
            i18n
          )
      }
    ]
  );
};

export const getThemeSettings = (i18n: I18n, templateId: string) => {
  const params = { templateId };
  return apiService.sendRequest(
    'shopify-integration.theme-integration.get-theme-settings',
    { params },
    [
      SI_THEME_INTEGRATION_SETTINGS_REQUEST,
      {
        type: SI_THEME_INTEGRATION_SETTINGS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_THEME_INTEGRATION_SETTINGS_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ThemeSetup.Actions.getAllThemes'),
            i18n
          )
      }
    ]
  );
};

/**
 * Call to update field in reducer
 * @returns An object with a type and a payload.
 */
export const handleUpdateField = (fieldName: string, data: any) => {
  return { type: SI_THEME_INTEGRATION_UPDATE_FIELD, payload: { fieldName, data } };
};

/**
 * Call to update multi fields in reducer
 * @returns An object with a type and a payload.
 */
export const handleUpdateMultiFields = (data: any) => {
  return { type: SI_THEME_INTEGRATION_UPDATE_MULTI_FIELDS, payload: { data } };
};

/**
 * call to add feature theme integration to specific store
 * @return {object} The RSAA object for API middleware
 */
export const themeIntegrationIntegrateFeature = (themeId: String, feature: String, i18n: I18n) => {
  const params = { themeId };
  const body = { feature };
  return apiService.sendRequest(
    'shopify-integration.theme-integration.theme-integration-feature',
    { params, body },
    [
      SI_THEME_INTEGRATION_FEATURE_REQUEST,
      {
        type: SI_THEME_INTEGRATION_FEATURE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_THEME_INTEGRATION_FEATURE_FAILURE,
        payload: async (action, state, res) => {
          const data = await getJSON(res);
          return { data, code: res.status };
        }
      }
    ]
  );
};
